import { defaultCurrency } from '@zapier/currency';

import {
  useQuery as defaultUseQuery,
  useMutation as defaultUseMutation,
} from '@apollo/react-hooks';

import {
  useCurrencies,
  useCurrentAccount,
  useNextPlan,
} from '@zapier/upsells/src/hooks';

import {
  ApolloInjection,
  BillingInterval,
  Currency,
} from '@zapier/upsells/src/types';

import { useAutoUpgradeFeatureEnrollment } from './useAutoUpgradeFeatureEnrollment';

type Props = {
  onSetAutoUpgradeFailure?: () => void;
  onSetAutoUpgradeSuccess?: () => void;
} & ApolloInjection;

export const useAutoUpgrade = ({
  onSetAutoUpgradeFailure = () => {},
  onSetAutoUpgradeSuccess = () => {},
  useMutation = defaultUseMutation,
  useQuery = defaultUseQuery,
}: Props = {}) => {
  const {
    currencies,
    error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useCurrencies({ useQuery });

  const {
    currentAccount,
    error: accountError,
    isLoading: isLoadingAccount,
  } = useCurrentAccount({ useQuery });

  const {
    error: nextPlanError,
    isLoading: isLoadingNextPlan,
    nextPlan,
  } = useNextPlan({
    accountId: currentAccount?.id,
    interval: BillingInterval.month,
    skip: !currentAccount?.id,
    useQuery,
  });

  const {
    error: autoUpgradeError,
    isAutoUpgradeEnabled,
    isEligibleForAutoUpgrade,
    isLoading: isLoadingAutoUpgrade,
    setAutoUpgrade,
  } = useAutoUpgradeFeatureEnrollment({
    accountId: currentAccount?.id,
    featureEnrollmentList: currentAccount?.featureEnrollmentList,
    onSetAutoUpgradeFailure,
    onSetAutoUpgradeSuccess,
    upgradePlanId: nextPlan?.id,
    useMutation,
  });

  return {
    account: currentAccount,
    accountCurrency:
      currencies?.find((c: Currency) => c.name === currentAccount?.currency) ??
      defaultCurrency,
    autoUpgradeToPlan: nextPlan,
    error: accountError ?? currenciesError ?? nextPlanError ?? autoUpgradeError,
    isAutoUpgradeEnabled,
    isEligibleForAutoUpgrade,
    isLoading:
      isLoadingAccount ||
      isLoadingCurrencies ||
      isLoadingNextPlan ||
      isLoadingAutoUpgrade,
    setAutoUpgrade,
  };
};
